import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Baby,
  GraduationCap,
  Buildings,
  Globe,
  Sun,
  Sparkle,
  HandHeart,
  Handshake,
  ArrowLeft,
  CheckCircle,
} from '@phosphor-icons/react';

const SECTORS = [
  { icon: Baby, label: 'رياض الأطفال والحضانات', en: 'Preschools & Nurseries' },
  { icon: GraduationCap, label: 'الجامعات والروضات التابعة لها', en: 'Campus Childcare' },
  { icon: Buildings, label: 'المؤسسات والهيئات الحكومية والخاصة', en: 'Public & Private Sector' },
  { icon: HandHeart, label: 'القطاع غير الربحي (الجمعيات والمؤسسات الأهلية ودور تحفيظ القرآن)', en: 'Nonprofit Sector' },
  { icon: Sparkle, label: 'نوادي الأطفال في الفنادق والمنتجعات', en: 'Kids Clubs' },
  { icon: Globe, label: 'الوجهات والمشروعات السياحية', en: 'Tourism Destinations' },
  { icon: Sun, label: 'المراكز والمخيمات الصيفية', en: 'Summer Camps' },
];

const WORKPLACE_SERVICES = [
  'دراسة الجاهزية والاحتياج',
  'تصميم البيئة التعليمية والتجهيزات',
  'النماذج التشغيلية والتنظيمية',
  'سياسات التشغيل والحوكمة',
  'تجربة الأسرة والتسجيل',
  'مرافقة المرحلة الأولى للتشغيل',
];

const TargetSectorsSection = () => (
  <section
    data-testid="target-sectors-section"
    className="py-20 bg-[#F5F2EC]"
    dir="rtl"
  >
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <motion.div
        className="text-center mb-12"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <span className="inline-block px-4 py-1.5 rounded-full border border-[#D8D2C8] text-[#5D6E55] text-xs sm:text-sm mb-4">
          من نخدم
        </span>
        <h2 className="font-['Noto_Kufi_Arabic'] font-black text-[#1F3D2B] text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight mb-4">
          القطاعات التي نخدمها
        </h2>
        <p className="text-[#6B7565] max-w-2xl mx-auto leading-8">
          خبراتنا موجّهة لكل جهة معنية بتطوير خدمات الطفولة المبكرة
        </p>
      </motion.div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {SECTORS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="bg-white rounded-2xl border border-[#E6E0D6] p-5 flex flex-col items-center text-center hover:border-[#4A641F] transition-colors"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#F5F2EC] flex items-center justify-center mb-3">
                <Icon size={22} weight="duotone" className="text-[#1F3D2B]" />
              </div>
              <p className="text-sm font-bold text-[#1F3D2B] leading-tight">
                {s.label}
              </p>
              <p className="mt-1.5 text-[10.5px] tracking-[0.14em] uppercase text-[#8B937F] font-medium">
                {s.en}
              </p>
            </motion.div>
          );
        })}
      </div>

      {/* Featured: Workplace Nurseries */}
      <motion.article
        data-testid="workplace-nurseries-spotlight"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-12 bg-white rounded-[24px] border border-[#E6E0D6] overflow-hidden shadow-sm"
      >
        <div className="grid lg:grid-cols-[1fr_1.2fr]">
          {/* Right column (RTL = visually right): identity + CTA */}
          <div className="bg-[#1F3D2B] text-white p-8 sm:p-10 flex flex-col justify-between order-1 lg:order-2">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center">
                  <Handshake size={24} weight="duotone" className="text-[#D8E0C9]" />
                </div>
                <span className="text-xs sm:text-sm text-[#D8E0C9] tracking-wide">
                  قطاع مؤسسي متخصص
                </span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold leading-tight font-['IBM_Plex_Sans_Arabic'] mb-4">
                استشارات تأسيس وتطوير الحضانات ورياض الأطفال
              </h3>
              <p className="text-sm sm:text-base text-[#D8E0C9] leading-8">
                حلول استشارية متكاملة لدعم تأسيس وتطوير الحضانات ورياض الأطفال،
                بدءًا من دراسة الجاهزية والاحتياج، مرورًا بتصميم النموذج التربوي
                والتشغيلي، ووصولًا إلى خطط الجاهزية ودعم الانطلاق وفق أفضل
                الممارسات.
              </p>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/sectors/workplace-nurseries"
                data-testid="workplace-nurseries-details-btn"
                className="flex-1 border border-white/40 text-white hover:bg-white/10 rounded-xl py-3 text-sm font-medium text-center transition-colors"
              >
                تفاصيل الخدمة
              </Link>
              <Link
                to="/quotation?service=workplace-nurseries"
                data-testid="workplace-nurseries-quote-btn"
                className="flex-1 bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 transition-colors"
              >
                اطلب عرض سعر
                <ArrowLeft size={16} className="rtl-flip" />
              </Link>
            </div>
          </div>

          {/* Left column: services list */}
          <div className="p-8 sm:p-10 order-2 lg:order-1">
            <h4 className="text-base sm:text-lg font-bold text-[#1F3D2B] mb-5 font-['IBM_Plex_Sans_Arabic']">
              نطاق العمل داخل هذا القطاع
            </h4>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {WORKPLACE_SERVICES.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2 text-sm text-[#3D512D] leading-7"
                >
                  <CheckCircle
                    size={16}
                    weight="fill"
                    className="text-[#4A641F] mt-1 shrink-0"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-[#92A082] leading-7">
              مرافقة استشارية كاملة من دراسة الاحتياج إلى التشغيل المنتظم، بمنهجية
              مؤسسية تتواءم مع طبيعة بيئة العمل ومتطلبات الأسر العاملة.
            </p>
          </div>
        </div>
      </motion.article>
    </div>
  </section>
);

export default TargetSectorsSection;
